import { Component, OnInit, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { ThemeServiceService } from 'src/app/services/theme-service.service';
import { Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;
}

@Component({
  selector: 'app-canvas',
  template: `<canvas #myCanvas></canvas>`,
  styleUrls: ['./canvas.component.css']
})
export class CanvasComponent implements OnInit, OnDestroy {

  @ViewChild('myCanvas', { static: true }) canvas: ElementRef<HTMLCanvasElement>;
  private ctx: CanvasRenderingContext2D;
  private nodes: Node[] = [];
  private numNodes: number = 80;
  private maxDistance: number = 150;
  private mouse = { x: 0, y: 0, radius: 150 };
  private destroy$ = new Subject<void>();
  
  // Colores para modo azul tecnológico
  private bluePrimaryColor = '37, 99, 235';    // #2563EB
  private blueAccentColor = '96, 165, 250';    // #60A5FA
  private blueDarkColor = '10, 14, 39';        // #0A0E27
  
  // Colores para modo monocromático
  private monoPrimaryColor = '0, 0, 0';        // Negro
  private monoAccentColor = '50, 50, 50';      // Gris oscuro
  private monoLightColor = '255, 255, 255';    // Blanco
  
  // Colores activos (se actualizan según el tema)
  private primaryColor = this.bluePrimaryColor;
  private accentColor = this.blueAccentColor;
  private darkColor = this.blueDarkColor;
  private bgColor1 = this.blueDarkColor;
  private bgColor2 = this.blueDarkColor;

  constructor(private themeService: ThemeServiceService) {}

  ngOnInit(): void {
    this.updateThemeColors();
    this.setupCanvas();
    this.createNodes();
    this.animateCanvas();

    // Verificar cambios de tema cada 500ms
    interval(500).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateThemeColors();
    });

    window.addEventListener('resize', () => {
      this.setupCanvas();
      this.createNodes();
    });

    // Tracking del mouse para interactividad
    window.addEventListener('mousemove', (e) => {
      this.mouse.x = e.x;
      this.mouse.y = e.y;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateThemeColors(): void {
    const activeTheme = this.themeService.getActiveTheme();
    const isMonochrome = activeTheme?.name === 'monochrome';
    
    if (isMonochrome) {
      this.primaryColor = this.monoPrimaryColor;
      this.accentColor = this.monoAccentColor;
      this.darkColor = this.monoLightColor;
      this.bgColor1 = this.monoLightColor;
      this.bgColor2 = '245, 245, 245'; // Blanco ligeramente gris
    } else {
      this.primaryColor = this.bluePrimaryColor;
      this.accentColor = this.blueAccentColor;
      this.darkColor = this.blueDarkColor;
      this.bgColor1 = this.blueDarkColor;
      this.bgColor2 = this.blueDarkColor;
    }
  }

  setupCanvas(): void {
    const canvasEl: HTMLCanvasElement = this.canvas.nativeElement;
    const context = canvasEl.getContext('2d');

    if (context) {
      this.ctx = context;
      canvasEl.width = window.innerWidth;
      canvasEl.height = window.innerHeight;
    } else {
      console.error('No se pudo obtener el contexto del canvas');
    }
  }

  createNodes(): void {
    this.nodes = [];
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    for (let i = 0; i < this.numNodes; i++) {
      this.nodes.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 2 + 1,
        opacity: Math.random() * 0.5 + 0.3
      });
    }
  }

  animateCanvas(): void {
    const animate = () => {
      if (!this.ctx) return;

      const width = window.innerWidth;
      const height = window.innerHeight;

      // Fondo con gradiente sutil
      const gradient = this.ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, `rgba(${this.bgColor1}, 1)`);
      gradient.addColorStop(1, `rgba(${this.bgColor2}, 0.95)`);
      this.ctx.fillStyle = gradient;
      this.ctx.fillRect(0, 0, width, height);

      // Actualizar y dibujar nodos
      this.nodes.forEach((node, i) => {
        // Movimiento
        node.x += node.vx;
        node.y += node.vy;

        // Rebote en bordes
        if (node.x < 0 || node.x > width) node.vx *= -1;
        if (node.y < 0 || node.y > height) node.vy *= -1;

        // Mantener dentro de límites
        node.x = Math.max(0, Math.min(width, node.x));
        node.y = Math.max(0, Math.min(height, node.y));

        // Dibujar conexiones
        this.nodes.slice(i + 1).forEach(otherNode => {
          const dx = node.x - otherNode.x;
          const dy = node.y - otherNode.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < this.maxDistance) {
            const opacity = (1 - distance / this.maxDistance) * 0.5;
            
            // Línea con gradiente
            const lineGradient = this.ctx.createLinearGradient(
              node.x, node.y, otherNode.x, otherNode.y
            );
            lineGradient.addColorStop(0, `rgba(${this.primaryColor}, ${opacity})`);
            lineGradient.addColorStop(0.5, `rgba(${this.accentColor}, ${opacity * 0.8})`);
            lineGradient.addColorStop(1, `rgba(${this.primaryColor}, ${opacity})`);
            
            this.ctx.strokeStyle = lineGradient;
            this.ctx.lineWidth = 0.5;
            this.ctx.beginPath();
            this.ctx.moveTo(node.x, node.y);
            this.ctx.lineTo(otherNode.x, otherNode.y);
            this.ctx.stroke();
          }
        });

        // Conexión con el mouse
        const dx = node.x - this.mouse.x;
        const dy = node.y - this.mouse.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < this.mouse.radius) {
          const opacity = (1 - distance / this.mouse.radius) * 0.6;
          this.ctx.strokeStyle = `rgba(${this.accentColor}, ${opacity})`;
          this.ctx.lineWidth = 1;
          this.ctx.beginPath();
          this.ctx.moveTo(node.x, node.y);
          this.ctx.lineTo(this.mouse.x, this.mouse.y);
          this.ctx.stroke();
        }

        // Dibujar nodos
        this.ctx.beginPath();
        this.ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        
        // Resplandor para nodos
        const nodeGradient = this.ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, node.radius * 3
        );
        nodeGradient.addColorStop(0, `rgba(${this.accentColor}, ${node.opacity})`);
        nodeGradient.addColorStop(0.5, `rgba(${this.primaryColor}, ${node.opacity * 0.5})`);
        nodeGradient.addColorStop(1, 'rgba(37, 99, 235, 0)');
        
        this.ctx.fillStyle = nodeGradient;
        this.ctx.fill();
        
        // Núcleo del nodo
        this.ctx.fillStyle = `rgba(${this.accentColor}, ${node.opacity + 0.3})`;
        this.ctx.fill();
      });

      requestAnimationFrame(animate);
    };

    animate();
  }
}
